import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    ContentLayout,
    Header,
    Container,
    SpaceBetween,
    Box,
    Button,
    Alert,
    Spinner
} from '@amzn/awsui-components-react';
import { useLazyGetS3FileQuery } from 'src/store/apis/testRunApi';
import { downloadFile } from '../common/DownloadFile';

export default function S3FilePreviewPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [getS3File, { data, isFetching, isError, error }] = useLazyGetS3FileQuery();
    const [s3Url, setS3Url] = useState<string | null>(null);

    useEffect(() => {
        const directory = searchParams.get('s3Dir');
        
        if (directory) {
            setS3Url(directory);
            getS3File({ s3Dir: directory });
        }
    }, [searchParams, getS3File]);

    const formatContent = (content: unknown): string => {
        if (typeof content === 'string') {
            try {
                const parsed = JSON.parse(content);
                return JSON.stringify(parsed, null, 2);
            } catch {
                return content;
            }
        }
        return JSON.stringify(content, null, 2);
    };

    const handleDownload = () => {
        if (!data || !s3Url) return;
        
        const content = formatContent(data);
        // Always use data.json as the filename since that's what the backend fetches
        const filename = 'data.json';
        downloadFile(content, filename);
    };

    return (
        <ContentLayout
            header={
                <Header
                    variant="h1"
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button 
                                onClick={handleDownload}
                                iconName="download"
                                disabled={!data || isFetching}
                            >
                                Download JSON
                            </Button>
                            <Button onClick={() => navigate('/testsuite')}>
                                Back to Dashboard
                            </Button>
                        </SpaceBetween>
                    }
                >
                    S3 File Preview
                </Header>
            }
        >
            <Container>
                <SpaceBetween size="m">
                    {s3Url && (
                        <Box variant="small" color="text-body-secondary">
                            {s3Url}
                        </Box>
                    )}

                    {!s3Url && (
                        <Alert type="warning">
                            No S3 URL provided in query parameters.
                        </Alert>
                    )}

                    {isFetching && (
                        <Box textAlign="center" padding={{ vertical: 'xxl' }}>
                            <Spinner size="large" />
                            <Box variant="p" padding={{ top: 's' }}>
                                Loading file content...
                            </Box>
                        </Box>
                    )}

                    {isError && (
                        <Alert type="error" header="Error loading file">
                            {error && 'data' in error && typeof error.data === 'object' && error.data !== null
                                ? JSON.stringify(error.data)
                                : 'Failed to load file from S3. Please ensure the file exists and you have proper permissions.'}
                        </Alert>
                    )}

                    {data && !isFetching && (
                        <Box>
                            <pre className="json-preview-pre">
                                {formatContent(data)}
                            </pre>
                        </Box>
                    )}
                </SpaceBetween>
            </Container>
        </ContentLayout>
    );
}